import { useState } from 'react';
import { Card, Checkbox, Button, Tag, Tooltip } from 'antd';
import { PlusOutlined, MinusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
    RiskSymbolCell,
    ActionLinkCell,
    SimpleTextCell,
} from '@client/components/ui/table/TableCells';
import { ProductTypeLegend } from '@client/components/ui/productLegends';
import RiskIndexDisplay from '@client/components/ui/RiskIndexDisplay';
import { getArlaRatingStyle } from '@client/utils/arlaRating';
import { getTreatmentBadges } from '@client/utils/treatmentValidity';
import IriisLink from '@client/components/ui/IriisLink';
import { HtmlContent } from '@client/utils/html_content';
import externalLink from '../../../../../../img/external-link.svg';
import chevronUpSvg from '../../../../../../img/product/chevron-up.svg';

const ChevronIcon = ({ expanded, className = '' }) => (
    <img
        src={chevronUpSvg}
        alt=""
        className={`transition-transform ${expanded ? '' : 'rotate-180'} ${className}`}
        width={16}
        height={9}
    />
);

const CollapsibleSection = ({ title, expanded, onToggle, children }) => (
    <div>
        <div
            className="bg-[#E9F1D9] rounded-[8px] px-4 py-3 cursor-pointer flex items-center justify-between"
            onClick={onToggle}
        >
            <span className="text-[16px] font-medium text-[#000]">{title}</span>
            <ChevronIcon expanded={expanded} />
        </div>
        {expanded && (
            <div className="px-4 py-4 space-y-6">{children}</div>
        )}
    </div>
);

// Simple table component matching the design
const SimpleTable = ({ headers, data, emptyMessage }) => {
    if (!data || data.length === 0) {
        return emptyMessage ? <p className="text-gray-500 text-sm">{emptyMessage}</p> : null;
    }

    return (
        <div className="w-full">
            {/* Header row */}
            <div className="flex bg-gray-100 rounded-t">
                {headers.map((header, index) => (
                    <div
                        key={index}
                        className={`py-3 px-4 font-medium text-gray-700 ${
                            index === 0 ? 'flex-1' : 'w-32 text-left'
                        }`}
                    >
                        {header}
                    </div>
                ))}
            </div>
            {/* Data rows */}
            {data.map((row, rowIndex) => (
                <div
                    key={rowIndex}
                    className="flex border-b border-gray-100 last:border-b-0"
                >
                    {row.map((cell, cellIndex) => (
                        <div
                            key={cellIndex}
                            className={`py-3 px-4 ${
                                cellIndex === 0 ? 'flex-1' : 'w-32 text-left'
                            }`}
                        >
                            {cell || '-'}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};

const TreatmentCard = ({ record, isSelected, onSelect, iriisLinks, searchMode = 'rapide', detailQuerySuffix = '' }) => {
    const { t } = useTranslation();
    const [openSection, setOpenSection] = useState(null);

    const toggleSection = (section) => {
        setOpenSection((prev) => (prev === section ? null : section));
    };

    // Toggle all panels
    const toggleAllPanels = () => {
        if (openSection) {
            setOpenSection(null);
        } else {
            setOpenSection('all');
        }
    };

    const isSectionExpanded = (key) => openSection === key || openSection === 'all';

    // Get validity badges (NOUVEAU, FIN, URGENCE)
    const badges = getTreatmentBadges(record);

    // Get cultures from treatment_cultures
    const getCultures = () => {
        const cultures = [];
        record.treatment_cultures?.forEach((tc) => {
            const culture = tc?.culture;
            if (culture) {
                const name = culture.french_name || culture.english_name || culture.latin_name;
                if (name && !cultures.find((c) => c.id === culture.id)) {
                    cultures.push({ id: culture.id, name });
                }
            }
        });
        return cultures;
    };

    // Get allergens from products
    const getAllergens = () => {
        const allergens = [];
        record.treatment_products?.forEach((tp) => {
            tp?.product?.allergens?.forEach((allergen) => {
                if (allergen?.name && !allergens.find((a) => a.id === allergen.id)) {
                    allergens.push(allergen);
                }
            });
        });
        return allergens;
    };

    // Get enemies with ratings
    const getEnemiesWithRatings = () => {
        const enemies = [];
        record.treatment_enemies?.forEach((te) => {
            if (te?.enemy_rating_ARLA) {
                enemies.push({
                    id: te.enemy?.id || te.id,
                    name: te.enemy?.client_display_name || te.enemy?.french_name || te.enemy?.english_name || te.enemy?.latin_name || te.enemy?.display_name,
                    enemy_category: te.enemy?.enemy_category?.name,
                    rating: te.enemy_rating_ARLA?.name,
                    rating_display: te.enemy_rating_ARLA?.client_display !== 'False' ? te.enemy_rating_ARLA?.client_display : null,
                    display_color: te.enemy_rating_ARLA?.display_color,
                });
            }
        });
        return enemies;
    };

    // Get toxicity to useful species from products
    const getToxicityToUsefulSpecies = () => {
        const toxicityData = [];
        record.treatment_products?.forEach((tp) => {
            tp?.product?.toxicity_to_non_target_species?.forEach((tox) => {
                if (tox?.names_of_useful_species) {
                    toxicityData.push({
                        id: tox.id,
                        species: tox.names_of_useful_species?.name || tox.names_of_useful_species?.french_name,
                        level: tox.toxicity_level?.name,
                        item_mentioned: tox.item_mentioned?.name,
                        comment: tox.comment,
                    });
                }
            });
        });
        return toxicityData;
    };

    const cultures = getCultures();
    const allergens = getAllergens();
    const enemiesWithRatings = getEnemiesWithRatings();
    const toxicityData = getToxicityToUsefulSpecies();

    // Helper to format delay display
    const formatDelay = (delay) => {
        if (!delay) return null;
        if (delay.name) return delay.name;
        if (delay.formatted_duration) return delay.formatted_duration;
        if (delay.display_value && delay.display_unit) {
            const unitLabels = { hours: 'h', days: 'j', weeks: 'sem' };
            return `${delay.display_value}${unitLabels[delay.display_unit] || delay.display_unit}`;
        }
        if (delay.duration_in_hours) {
            if (delay.duration_in_hours < 24) return `${delay.duration_in_hours}h`;
            return `${Math.round(delay.duration_in_hours / 24)} j`;
        }
        return delay.value;
    };

    // Prepare DS data for simple table
    const dsTableData = record.treatment_security_delays?.map((sd) => [
        sd.activity_associated_with_security_delay?.name || '-',
        formatDelay(sd.security_delay),
    ]) || [];

    // Prepare DAAR data for simple table
    const daarTableData = record.treatment_activity_associated_with_preharvest_waiting_times?.map((aw) => [
        aw.activity_associated_with_preharvest_waiting_time?.name || '-',
        formatDelay(aw.delay_before_harvest),
    ]) || [];

    // Prepare enemies data for simple table
    const enemiesTableData = enemiesWithRatings.map((e) => [
        <div key={e.id}>
            {e.enemy_category && (
                <span className="text-gray-500 text-[12px]">{e.enemy_category}</span>
            )}
            {e.enemy_category && <br />}
            <IriisLink url={iriisLinks?.[e.id]?.url}>
                {e.name || '-'}
            </IriisLink>
        </div>,
        e.rating ? (
            <span
                className="inline-flex flex-col justify-center items-center rounded-sm text-xs"
                style={{ padding: '4px 8px', ...getArlaRatingStyle(e.display_color) }}
            >
                {e.rating}
            </span>
        ) : '-',
    ]);

    // Prepare toxicity data for simple table
    const toxicityTableData = toxicityData.map((tox) => [
        tox.species || '-',
        tox.level || '-',
    ]);

    return (
        <Card
            className="treatment-card mb-4 shadow-sm hover:shadow-md transition-shadow"
        >
            {/* Card Header */}
            <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3 flex-wrap">
                    <Checkbox
                        checked={isSelected}
                        onChange={(e) => onSelect(record.id, e.target.checked)}
                    />
                    <ActionLinkCell
                        href={`/traitement/details/${record.id}?from=${searchMode === 'extraction' ? 'multichoix' : searchMode}${detailQuerySuffix}`}
                        target="_blank"
                        className="btn btn--primary hover:text-[#000] gap-2"
                    >
                        <img src={externalLink} alt="External link" />
                        {t('treatment-search|Traitement complet')}
                    </ActionLinkCell>
                    {badges.map((badge) => (
                        <Tooltip
                            key={badge.key}
                            title={t(`treatment-search|${badge.tooltipKey}`)}
                        >
                            <span
                                className="text-[10px] font-bold rounded px-1.5 py-0.5 leading-tight cursor-help"
                                style={{ backgroundColor: badge.bgColor, color: badge.textColor }}
                            >
                                {t(`treatment-search|${badge.labelKey}`)}
                            </span>
                        </Tooltip>
                    ))}
                    {record?.agronomic_prescription_icon_be_displayed_on_client_site && (
                        <Tag color="#FA8C16" className="rounded text-[11px] font-bold">
                            PRESCRIPTION
                        </Tag>
                    )}
                </div>
                <Button
                    type="primary"
                    shape="circle"
                    onClick={toggleAllPanels}
                    icon={openSection ? <MinusOutlined /> : <PlusOutlined />}
                    size="small"
                    className="bg-[#A0D911] hover:bg-[#7CB305] border-none"
                    style={{ minWidth: '32px', width: '32px', height: '32px' }}
                />
            </div>

            {/* Collapsible Sections */}
            <div className="space-y-4">
                {/* Risques et délais Section */}
                <CollapsibleSection
                    title={t('treatment-search|Risques et délais')}
                    expanded={isSectionExpanded('risks_delays')}
                    onToggle={() => toggleSection('risks_delays')}
                >
                    {/* Risk Indices and Symbols */}
                    <div className="flex gap-6 flex-col">
                        {/* Health Risk Index */}
                        <div className="flex items-center gap-4">
                            <div className="min-w-[120px]">
                                <span className="text-[16px] font-bold text-[#000]">IRS : <RiskIndexDisplay value={record?.health_risk_index} isExpert={record?.health_risk_index_is_expert} variant="inline" /></span>
                            </div>
                            <div className="flex gap-2 items-center">
                                <RiskSymbolCell
                                    symbolData={record.symbols?.health?.acute_toxicity_symbol}
                                    alt="Acute toxicity"
                                />
                                <RiskSymbolCell
                                    symbolData={record.symbols?.health?.long_term_effect_symbol}
                                    alt="Long term effects"
                                />
                            </div>
                        </div>

                        {/* Environment Risk Index */}
                        <div className="flex items-center gap-4">
                            <div className="min-w-[120px]">
                                <span className="text-[16px] font-bold text-[#000]">IRE : <RiskIndexDisplay value={record?.environment_risk_index} isExpert={record?.environment_risk_index_is_expert} variant="inline" /></span>
                            </div>
                            <div className="flex gap-2 items-center">
                                <RiskSymbolCell
                                    symbolData={record.symbols?.environment?.fishes_symbol}
                                    alt="Fish or daphnia"
                                />
                                <RiskSymbolCell
                                    symbolData={record.symbols?.environment?.birds_symbol}
                                    alt="Birds"
                                />
                                <RiskSymbolCell
                                    symbolData={record.symbols?.environment?.bees_symbol}
                                    alt="Bees"
                                />
                                <RiskSymbolCell
                                    symbolData={record.symbols?.environment?.persistence_symbol}
                                    alt="Persistence"
                                />
                                <RiskSymbolCell
                                    symbolData={record.symbols?.environment?.leaching_symbol}
                                    alt="Leaching potential"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Intervention Period */}
                    {(record?.intervention_period?.display_name || record?.intervention_period?.name) && (
                        <div className="mt-4">
                            <p className="text-[16px] font-bold mb-2">
                                {t('treatment-search|Période d\'intervention')}
                            </p>
                            <p className="text-sm text-gray-600 px-4">
                                {record.intervention_period.display_name || record.intervention_period.name}
                            </p>
                        </div>
                    )}

                    {/* Product Information */}
                    {record?.treatment_products?.length > 0 && (
                        <div className="mt-4">
                            <p className="text-[16px] font-bold mb-2">
                                {t('treatment-search|Produit commercial ou série')}
                            </p>
                            {record.treatment_products.map((tp, index) => (
                                <div key={index} className="mb-3 p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {(() => {
                                            const link = (
                                                <a
                                                    href={tp?.selection_type === 'series'
                                                        ? `/series/${tp?.product?.id}/`
                                                        : `/produits-commerciaux/details/${tp?.product?.id}/`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-[#105235] font-bold text-[14px] hover:underline"
                                                >
                                                    {tp?.product?.name || tp?.series?.name}
                                                </a>
                                            );
                                            const members = tp?.selection_type === 'series'
                                                ? (tp?.product?.member_products || [])
                                                : [];
                                            if (!members.length) return link;
                                            return (
                                                <Tooltip
                                                    title={(
                                                        <div>
                                                            <div className="font-semibold mb-1">
                                                                {t('legends.serieMemberProducts', 'Produits commerciaux de la série')}
                                                            </div>
                                                            <ul className="list-disc pl-4 mb-0">
                                                                {members.map((m) => (
                                                                    <li key={m.id}>
                                                                        {m.name}{m.approval_number ? ` (${m.approval_number})` : ''}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                >
                                                    {link}
                                                </Tooltip>
                                            );
                                        })()}
                                        {tp?.selection_type === 'series' && (
                                            <ProductTypeLegend type="serie" />
                                        )}
                                        {tp?.product?.is_organic_approval && (
                                            <ProductTypeLegend type="biological" height={22} />
                                        )}
                                    </div>
                                    {/* Dose */}
                                    {(Number(tp?.min_dose) || Number(tp?.max_dose)) && (
                                        <div className="text-[13px] text-gray-600 mt-1">
                                            <span className="font-medium">{t('treatment-search|Dose')} : </span>
                                            {Number(tp.min_dose) && Number(tp.max_dose) && Number(tp.min_dose) !== Number(tp.max_dose)
                                                ? `${tp.min_dose} - ${tp.max_dose}`
                                                : Number(tp.max_dose) ? tp.max_dose : tp.min_dose
                                            }
                                            {tp?.units?.name && ` ${tp.units.name}`}
                                        </div>
                                    )}
                                    {/* Active Substances for this product */}
                                    {tp?.product?.active_substances?.length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                                            {tp.product.active_substances.map((asp, i) => {
                                                const substance = asp?.active_substance;
                                                const pesticideGroups = substance?.pesticide_group_associations
                                                    ?.map((assoc) => assoc.pesticide_group?.name)
                                                    .filter(Boolean)
                                                    .join(', ');
                                                return (
                                                    <span key={substance?.id || i} className="text-[13px]">
                                                        <a
                                                            href={`/matieres-actives/details/${substance?.id}/`}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="text-[#105235] font-bold hover:underline"
                                                        >
                                                            <HtmlContent html={substance?.name} />
                                                        </a>
                                                        {pesticideGroups && (
                                                            <span className="text-gray-500"> ({pesticideGroups})</span>
                                                        )}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Activity Types (Préventif/Curatif) */}
                    {record?.treatment_products?.length > 0 && (
                        (() => {
                            const activityTypesMap = new Map();
                            record.treatment_products.forEach((tp) => {
                                tp?.product?.active_substances?.forEach((asp) => {
                                    asp?.active_substance?.activity_types?.forEach((at) => {
                                        if (at?.short_name && !activityTypesMap.has(at.id)) {
                                            activityTypesMap.set(at.id, at);
                                        }
                                    });
                                });
                            });
                            const uniqueTypes = Array.from(activityTypesMap.values());
                            if (uniqueTypes.length === 0) return null;
                            return (
                                <div className="mt-4">
                                    <p className="text-[16px] font-bold mb-2">
                                        {t('treatment-search|Type d\'activité')}
                                    </p>
                                    <div className="flex flex-wrap gap-2 px-4">
                                        {uniqueTypes.map((at) => (
                                            <Tag key={at.id}>{at.short_name}</Tag>
                                        ))}
                                    </div>
                                </div>
                            );
                        })()
                    )}

                    {/* Weed Stage (herbicide) */}
                    {(record?.weed_stage?.display_label || record?.weed_stage?.label) && (
                        <div className="mt-4">
                            <p className="text-[16px] font-bold mb-2">
                                {t('treatment-search|Stade des mauvaises herbes')}
                            </p>
                            <p className="text-sm text-gray-600 px-4">
                                {record.weed_stage.display_label || record.weed_stage.label}
                            </p>
                        </div>
                    )}

                    {/* Weed Groups (herbicide) */}
                    {(() => {
                        const WEED_GROUPS = [
                            { key: 'annual_grasses', label: 'Graminées annuelles', matchLabels: ['Graminées annuelles'] },
                            { key: 'annual_broadleaf', label: 'Feuilles larges annuelles', matchLabels: ['Feuilles larges annuelles'] },
                            { key: 'perennial_grasses', label: 'Graminées vivaces', matchLabels: ['Graminées vivaces'] },
                            { key: 'perennial_broadleaf', label: 'Feuilles larges vivaces', matchLabels: ['Feuilles larges vivaces'] },
                            { key: 'perennial', label: 'Vivaces', matchLabels: ['Vivaces'] },
                        ];
                        const presentGroups = WEED_GROUPS.filter((group) =>
                            record.treatment_enemies?.some(
                                (te) => te.enemy?.weed_group?.label &&
                                    group.matchLabels.includes(te.enemy.weed_group.label)
                            )
                        );
                        if (presentGroups.length === 0) return null;
                        return (
                            <div className="mt-4">
                                <p className="text-[16px] font-bold mb-2">
                                    {t('treatment-search|Groupes de mauvaises herbes')}
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {presentGroups.map((group) => (
                                        <Tag key={group.key}>{group.label}</Tag>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}

                    {/* Cultures */}
                    {cultures.length > 0 && (
                        <div className="mt-4">
                            <p className="text-[16px] font-bold mb-2">
                                {t('treatment-search|Cultures')}
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {cultures.map((culture) => (
                                    <Tag key={culture.id} color="green">
                                        {culture.name}
                                    </Tag>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Allergens */}
                    {allergens.length > 0 && (
                        <div className="mt-4">
                            <p className="text-[16px] font-bold mb-2">
                                {t('treatment-search|Allergènes')}
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {allergens.map((allergen, index) => (
                                    <Tag key={allergen.id || index}>
                                        {allergen.name}
                                    </Tag>
                                ))}
                            </div>
                        </div>
                    )}
                </CollapsibleSection>

                {/* Délais Section */}
                <CollapsibleSection
                    title={t('treatment-search|Délais')}
                    expanded={isSectionExpanded('delays')}
                    onToggle={() => toggleSection('delays')}
                >
                    {/* Security Delays (DS) */}
                    <div>
                        <p className="text-[16px] font-bold mb-3">
                            {t('treatment-search|Délais de sécurité (DS)')} :
                        </p>
                        {dsTableData.length > 0 ? (
                            <SimpleTable
                                headers={[t('treatment-search|Activité'), 'DS']}
                                data={dsTableData}
                            />
                        ) : (
                            <SimpleTextCell text={record?.security_delays_combined} />
                        )}
                    </div>

                    {/* DAAR */}
                    <div>
                        <p className="text-[16px] font-bold mb-3">
                            {t("treatment-search|Délais d'attente avant récolte (DAAR)")} :
                        </p>
                        {daarTableData.length > 0 ? (
                            <SimpleTable
                                headers={[t('treatment-search|Activité'), 'DAAR']}
                                data={daarTableData}
                            />
                        ) : (
                            <SimpleTextCell text={record?.delays_before_harvest_combined} />
                        )}
                    </div>

                    {/* Délai avant la pluie */}
                    {record?.delay_before_rain && (
                        <div>
                            <p className="text-[16px] font-bold mb-2">
                                {t('treatment-search|Délais avant la pluie')}:
                            </p>
                            <p className="px-4">{record.delay_before_rain}</p>
                        </div>
                    )}

                    {/* Délai de rentrée */}
                    {record?.re_entry_deadline && (
                        <div>
                            <p className="text-[16px] font-bold mb-2">
                                {t('treatment-search|Délai de rentrée')}:
                            </p>
                            <p className="px-4">{formatDelay(record.re_entry_deadline)}</p>
                        </div>
                    )}
                </CollapsibleSection>

                {/* Ennemis Section */}
                {enemiesWithRatings.length > 0 && (
                    <CollapsibleSection
                        title={t('treatment-search|Ennemis')}
                        expanded={isSectionExpanded('enemies')}
                        onToggle={() => toggleSection('enemies')}
                    >
                        <SimpleTable
                            headers={[t('treatment-search|Ennemi'), t('treatment-search|Efficacité ARLA')]}
                            data={enemiesTableData}
                        />
                    </CollapsibleSection>
                )}

                {/* Toxicité sur les espèces utiles Section */}
                <CollapsibleSection
                    title={t('treatment-search|Toxicité sur les espèces utiles')}
                    expanded={isSectionExpanded('toxicity')}
                    onToggle={() => toggleSection('toxicity')}
                >
                    {toxicityTableData.length > 0 ? (
                        <SimpleTable
                            headers={[t('treatment-search|Espèce utile'), t('treatment-search|Niveau de toxicité')]}
                            data={toxicityTableData}
                        />
                    ) : (
                        <div className="space-y-4">
                            <p className="text-[16px] font-bold">{t("treatment-search|Selon l'étiquette")}:</p>
                            <p className="text-sm text-gray-600 px-4">
                                {t('treatment-search|Aucune donnée de toxicité disponible')}
                            </p>
                        </div>
                    )}
                </CollapsibleSection>
            </div>
        </Card>
    );
};

export default TreatmentCard;
